import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { ShoppingBag, Search, Package, X, CheckCircle2, Circle, Calendar, Receipt } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import API_BASE_URL from '../../config/api';
import InvoiceView from '../../components/InvoiceView';

const ProductOrders = () => { // Repurposed for Shop Orders
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewOrder, setViewOrder] = useState(null);
    const [updateModal, setUpdateModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusUpdate, setStatusUpdate] = useState({ status: '', tracking_number: '', courier_name: '' });
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { showAlert } = useAlert();

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/shop/orders.php`);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            url.searchParams.append('_t', Date.now());

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setOrders(data.data);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            showAlert('Failed to load orders', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateClick = (order) => {
        setSelectedOrder(order);
        setStatusUpdate({
            status: order.status,
            tracking_number: order.tracking_number || '',
            courier_name: order.courier_name || ''
        });
        setUpdateModal(true);
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('authToken');
            const ids = isGrouped ? selectedOrder.original_ids : [selectedOrder.id];

            const response = await fetch(`${API_BASE_URL}/admin/shop/update_order.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ids, // Send as array
                    ...statusUpdate
                })
            });
            const data = await response.json();
            if (data.success) {
                showAlert(`${ids.length} order(s) updated successfully`, 'success');
                setUpdateModal(false);
                fetchOrders();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            showAlert('Update failed', 'error');
        }
    };

    const [isGrouped, setIsGrouped] = useState(false);
    const [viewInvoiceOrderId, setViewInvoiceOrderId] = useState(null);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'bg-yellow-100 text-yellow-800';
            case 'Approved': return 'bg-blue-100 text-blue-800';
            case 'Ready to Dispatch': return 'bg-purple-100 text-purple-800';
            case 'Shipped': return 'bg-indigo-100 text-indigo-800';
            case 'Delivered': return 'bg-green-100 text-green-800';
            case 'Cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const processOrders = () => {
        let filtered = orders.filter(o =>
            o.order_unique_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            o.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (o.franchise_owner_name && o.franchise_owner_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (o.franchise_user_code && o.franchise_user_code.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        if (isGrouped) {
            const grouped = {};
            filtered.forEach(order => {
                const key = `${order.user_email}-${order.status}-${order.shipping_address}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        ...order,
                        id: `GRP-${order.id}`, // Virtual ID
                        original_ids: [order.id],
                        order_unique_ids: [order.order_unique_id],
                        total_amount: parseFloat(order.total_amount),
                        items_summary: order.items_summary,
                        order_count: 1
                    };
                } else {
                    grouped[key].total_amount += parseFloat(order.total_amount);
                    grouped[key].items_summary += ', ' + order.items_summary;
                    grouped[key].order_unique_ids.push(order.order_unique_id);
                    grouped[key].order_count += 1;
                }
            });
            return Object.values(grouped);
        }

        return filtered;
    };

    const StatusTimeline = ({ currentStatus }) => {
        const statuses = [
            { label: 'Pending', key: 'Pending' },
            { label: 'Approved', key: 'Approved' },
            { label: 'Ready to Dispatch', key: 'Ready to Dispatch' },
            { label: 'Shipped', key: 'Shipped' },
            { label: 'Delivered', key: 'Delivered' }
        ];

        const currentIndex = statuses.findIndex(s => s.key === currentStatus);

        if (currentStatus === 'Cancelled') {
            return (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                    <X className="bg-red-600 text-white rounded-full p-1" size={24} />
                    <div>
                        <p className="font-bold">Order Cancelled</p>
                        <p className="text-sm">This order has been cancelled.</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {statuses.map((s, idx) => {
                    const isCompleted = idx < currentIndex;
                    const isCurrent = idx === currentIndex;
                    const isLast = idx === statuses.length - 1;

                    return (
                        <div key={s.key} className="relative flex gap-4">
                            {!isLast && (
                                <div className={`absolute left-3 top-6 w-0.5 h-10 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
                            )}
                            <div className="z-10 bg-white">
                                {isCompleted ? (
                                    <CheckCircle2 className="text-green-500" size={24} />
                                ) : isCurrent ? (
                                    <div className="h-6 w-6 rounded-full border-4 border-blue-600 flex items-center justify-center animate-pulse" />
                                ) : (
                                    <Circle className="text-gray-300" size={24} />
                                )}
                            </div>
                            <div className="pb-4">
                                <p className={`text-xs font-black uppercase tracking-widest ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {s.label}
                                </p>
                                {isCurrent && (
                                    <p className="text-[10px] text-gray-500 font-bold mt-0.5">Current stage of fulfillment</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const filteredOrders = processOrders();

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-black text-gray-900 flex items-center gap-2">
                            <ShoppingBag className="text-blue-600" size={24} />
                            Shop Orders
                        </h1>
                        <p className="text-gray-500 text-sm font-medium">Manage customer product orders</p>
                    </div>
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col lg:flex-row justify-between items-center gap-4 mb-4">
                        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                                />
                            </div>
                            <span className="text-gray-300 text-xs font-black">to</span>
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                                />
                            </div>

                            <button
                                onClick={fetchOrders}
                                className="px-4 py-2.5 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100"
                            >
                                Filter
                            </button>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                            <button
                                onClick={() => setIsGrouped(!isGrouped)}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-2 flex items-center gap-2 ${isGrouped
                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100'
                                    : 'bg-white border-gray-100 text-gray-500 hover:border-indigo-200'}`}
                            >
                                <Package size={16} />
                                {isGrouped ? 'Grouped Packing' : 'Single Orders'}
                            </button>
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search ID or Email..."
                                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-xs transition-all font-bold"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        {/* Mobile Grid View */}
                        <div className="grid grid-cols-1 gap-4 lg:hidden">
                            {filteredOrders.length > 0 ? filteredOrders.map(order => (
                                <div key={order.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-3">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                {isGrouped && order.order_count > 1 ? 'Combined Orders' : 'Order ID'}
                                            </div>
                                            <div className="font-black text-gray-900">
                                                {isGrouped && order.order_count > 1
                                                    ? `${order.order_unique_ids[0]} +${order.order_count - 1}`
                                                    : order.order_unique_id}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${getStatusColor(order.status)}`}>
                                                {order.status}
                                            </span>
                                            {isGrouped && order.order_count > 1 && (
                                                <span className="text-[9px] font-black bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md uppercase tracking-tight">
                                                    Grouping Enabled
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-50">
                                        <div>
                                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total</div>
                                            <div className="font-black text-blue-600">₹{parseFloat(order.total_amount).toLocaleString()}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Date</div>
                                            <div className="font-bold text-gray-700 text-sm">{new Date(order.created_at).toLocaleDateString()}</div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Customer</div>
                                        <div className="font-bold text-gray-900 text-sm truncate">{order.user_email}</div>
                                        <div className="text-xs text-gray-500">{order.user_mobile}</div>
                                    </div>

                                    {order.fulfillment_source === 'franchise' && (
                                        <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">
                                            <div className="text-[10px] font-bold text-orange-400 uppercase tracking-widest mb-1">🏪 Franchise Distributor</div>
                                            <div className="font-bold text-orange-800 text-sm">{order.franchise_owner_name || order.fulfilled_by_franchise || 'N/A'}</div>
                                            {order.franchise_user_code && (
                                                <div className="text-[10px] font-bold text-orange-600 mt-0.5">ID: {order.franchise_user_code}</div>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex gap-2 pt-2">
                                        <button
                                            onClick={() => setViewOrder(order)}
                                            className="flex-1 py-2 bg-blue-50 text-blue-600 rounded-xl font-bold text-xs flex items-center justify-center gap-2 shadow-sm border border-blue-100 hover:bg-blue-100 transition-all"
                                        >
                                            <Package size={14} /> View Items ({isGrouped ? order.order_count : 1})
                                        </button>
                                        <button
                                            onClick={() => handleUpdateClick(order)}
                                            className="flex-1 py-2 bg-gray-900 text-white rounded-xl font-bold text-xs shadow-lg shadow-gray-200 hover:bg-black transition-all"
                                        >
                                            Update Status
                                        </button>
                                    </div>
                                </div>
                            )) : (
                                <div className="p-8 text-center bg-white rounded-2xl border border-dashed text-gray-500 font-bold uppercase tracking-widest text-xs">No orders found</div>
                            )}
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500">Order ID</th>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500">User</th>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500">Total</th>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500">Fulfillment</th>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500">Status</th>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500">Date</th>
                                        <th className="p-4 font-bold text-xs uppercase tracking-widest text-gray-500 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredOrders.length > 0 ? filteredOrders.map(order => (
                                        <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-black text-gray-900">
                                                    {isGrouped && order.order_count > 1
                                                        ? order.order_unique_ids[0]
                                                        : order.order_unique_id}
                                                </div>
                                                {isGrouped && order.order_count > 1 && (
                                                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 mt-1 bg-indigo-50 text-indigo-600 rounded-md text-[9px] font-black uppercase tracking-tighter">
                                                        <Package size={10} /> +{order.order_count - 1} Combined
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900 text-sm truncate max-w-[150px]">{order.user_email}</div>
                                                <div className="text-xs text-gray-500">{order.user_mobile}</div>
                                            </td>

                                            <td className="p-4">
                                                <div className="font-black text-blue-600">₹{parseFloat(order.total_amount).toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Total Value</div>
                                            </td>
                                            <td className="p-4">
                                                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${order.fulfillment_source === 'franchise' ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
                                                    {order.fulfillment_source === 'franchise' ? '🏪 Franchise' : '🏢 Admin'}
                                                </div>
                                                {order.fulfillment_source === 'franchise' && (
                                                    <div className="mt-1.5 space-y-0.5">
                                                        <div className="text-[11px] font-bold text-gray-700 truncate max-w-[160px]" title={order.franchise_owner_name || order.fulfilled_by_franchise}>
                                                            {order.franchise_owner_name || order.fulfilled_by_franchise || 'N/A'}
                                                        </div>
                                                        {order.franchise_user_code && (
                                                            <div className="inline-flex items-center px-1.5 py-0.5 bg-orange-50 border border-orange-100 rounded text-[9px] font-black text-orange-600">
                                                                ID: {order.franchise_user_code}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${getStatusColor(order.status)}`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="p-4 text-xs font-bold text-gray-500">
                                                {new Date(order.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex gap-2 justify-end">
                                                    <button
                                                        onClick={() => setViewOrder(order)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl border border-transparent hover:border-blue-100 transition-all"
                                                        title="View Packing List"
                                                    >
                                                        <Package size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateClick(order)}
                                                        className="px-4 py-2 text-xs bg-gray-900 text-white rounded-xl hover:bg-black shadow-md shadow-gray-100 transition-all font-black uppercase tracking-widest"
                                                    >
                                                        Update
                                                    </button>
                                                    {['Approved', 'Ready to Dispatch', 'Shipped', 'Delivered'].includes(order.status) && (
                                                        <button
                                                            onClick={() => setViewInvoiceOrderId(isGrouped ? order.original_ids?.[0] : order.id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-xl border border-transparent hover:border-red-100 transition-all"
                                                            title="View Invoice"
                                                        >
                                                            <Receipt size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="6" className="p-12 text-center text-gray-400 font-black uppercase tracking-widest text-sm">No orders found 🔍</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* View Details Modal */}
                {viewOrder && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
                            <div className="p-5 border-b flex justify-between items-center bg-gray-50/50">
                                <h3 className="font-black text-gray-900">
                                    {isGrouped && viewOrder.order_count > 1 ? 'Combined Packing List' : 'Order Details'}
                                </h3>
                                <button onClick={() => setViewOrder(null)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                    <X className="text-gray-500" size={20} />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto space-y-6">
                                {isGrouped && viewOrder.order_count > 1 && (
                                    <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-2xl">
                                        <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Orders in this Shipment</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {viewOrder.order_unique_ids.map(id => (
                                                <span key={id} className="text-xs font-black bg-white text-indigo-600 px-2 py-1 rounded-lg border border-indigo-100 shadow-sm">
                                                    #{id}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Shipping Information</h4>
                                    <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl">
                                        <p className="font-bold text-gray-900 mb-1">{viewOrder.shipping_name}</p>
                                        <p className="text-sm text-gray-600 leading-relaxed">{viewOrder.shipping_address}</p>
                                        <p className="text-sm text-gray-600 font-medium mt-1">{viewOrder.shipping_city}, {viewOrder.shipping_state} - {viewOrder.shipping_zip}</p>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="text-sm text-blue-700 font-bold">
                                                <span>📞</span> {viewOrder.shipping_mobile}
                                            </div>
                                            <div className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${viewOrder.fulfillment_source === 'franchise' ? 'bg-orange-600 text-white' : 'bg-blue-600 text-white'}`}>
                                                {viewOrder.fulfillment_source === 'franchise' ? `Franchise: ${viewOrder.fulfilled_by_franchise || 'Assigned'}` : 'Admin Direct'}
                                            </div>
                                        </div>
                                        {viewOrder.fulfillment_source === 'franchise' && (
                                            <div className="mt-3 bg-orange-50 border border-orange-200 rounded-xl p-3">
                                                <div className="text-[10px] font-black text-orange-400 uppercase tracking-widest mb-1">Distributor Info</div>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-bold text-orange-900 text-sm">{viewOrder.franchise_owner_name || viewOrder.fulfilled_by_franchise || 'N/A'}</div>
                                                        {viewOrder.franchise_user_code && (
                                                            <div className="text-[11px] font-bold text-orange-600">ID: {viewOrder.franchise_user_code}</div>
                                                        )}
                                                    </div>
                                                    <div className="h-8 w-8 bg-orange-200 rounded-lg flex items-center justify-center text-orange-700 text-sm font-black">
                                                        🏪
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Fulfillment Progress</h4>
                                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
                                        <StatusTimeline currentStatus={viewOrder.status} />

                                        {(viewOrder.status === 'Shipped' || viewOrder.status === 'Delivered') && viewOrder.tracking_number && (
                                            <div className="mt-6 pt-6 border-t border-gray-200/50 flex gap-4 items-center animate-in fade-in slide-in-from-top-2 duration-500">
                                                <div className="h-10 w-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 shadow-sm">
                                                    <Package size={20} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-0.5">Courier Details</p>
                                                    <p className="text-sm font-black text-blue-900">{viewOrder.courier_name}</p>
                                                    <p className="text-xs text-blue-700 font-bold">Tracking: <span className="select-all">{viewOrder.tracking_number}</span></p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Order Items</h4>
                                    <div className="space-y-2">
                                        {viewOrder.items_summary.split(', ').map((item, idx) => (
                                            <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-xs font-bold text-blue-600 border border-gray-200">
                                                    {idx + 1}
                                                </div>
                                                <div className="text-sm font-bold text-gray-800">{item}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 bg-gray-50 border-t">
                                <button
                                    onClick={() => setViewOrder(null)}
                                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold shadow-lg shadow-gray-200"
                                >
                                    Close Details
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Update Modal */}
                {updateModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-black text-gray-900">Update Order</h3>
                                <button onClick={() => setUpdateModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                                    <X size={20} className="text-gray-400" />
                                </button>
                            </div>

                            <div className="mb-6 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Selected Order</div>
                                <div className="font-black text-gray-900">{selectedOrder?.order_unique_id}</div>
                            </div>

                            <form onSubmit={handleUpdateSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">New Status</label>
                                    <div className="relative">
                                        <select
                                            className="w-full bg-gray-50 border border-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-bold text-sm appearance-none"
                                            value={statusUpdate.status}
                                            onChange={e => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                                        >
                                            <option value="Pending">Pending</option>
                                            <option value="Approved">Approved</option>
                                            <option value="Ready to Dispatch">Ready to Dispatch</option>
                                            <option value="Shipped">Shipped</option>
                                            <option value="Delivered">Delivered</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </select>
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">▼</div>
                                    </div>
                                </div>

                                {['Shipped', 'Delivered'].includes(statusUpdate.status) && (
                                    <div className="space-y-4 animate-in fade-in duration-300">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Courier Partner</label>
                                            <input
                                                placeholder="e.g. BlueDart, Delhivery"
                                                className="w-full bg-gray-50 border border-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium"
                                                value={statusUpdate.courier_name}
                                                onChange={e => setStatusUpdate({ ...statusUpdate, courier_name: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Tracking ID</label>
                                            <input
                                                placeholder="Enter tracking number"
                                                className="w-full bg-gray-50 border border-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium"
                                                value={statusUpdate.tracking_number}
                                                onChange={e => setStatusUpdate({ ...statusUpdate, tracking_number: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setUpdateModal(false)}
                                        className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            {/* Invoice View Modal */}
            {viewInvoiceOrderId && (
                <InvoiceView
                    referenceId={viewInvoiceOrderId}
                    invoiceType="product"
                    onClose={() => setViewInvoiceOrderId(null)}
                    isAdmin={true}
                />
            )}
        </Layout>
    );
};

export default ProductOrders;


